/**
 * ConnectionContext — WebSocket lifecycle for LAUNCHER-2.0.
 *
 * Owns the singleton OrchestratorClient instance, auto-connects on mount,
 * and exposes connection state to the rest of the tree.
 *
 * Exposes:
 *   client           — OrchestratorClient singleton
 *   connectionStatus — 'disconnected' | 'connecting' | 'connected' | 'error'
 *   setConnectionStatus — setter (used by MessageContext on WS errors)
 *   isConnected      — convenience boolean
 *   ensureConnected()— connect if not already connected (await-able)
 *   health           — { connectedSince, disconnectedAt, reconnectCount, messageThroughput }
 *   perf             — usePerformance() handle for latency / reconnect tracking
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getClient } from '../services/orchestratorClient';
import { usePerformance } from '../hooks/usePerformance';
import { createLogger } from '../utils/logger';

const log = createLogger('ConnectionContext');

const ConnectionContext = createContext(null);

export function ConnectionProvider({ children }) {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const clientRef = useRef(getClient());
  const perf = usePerformance();

  // ── Connection health state ─────────────────────────────────────────────
  const healthRef = useRef({
    connectedSince: null,
    disconnectedAt: null,
    reconnectCount: 0,
    messagesReceived: 0,
  });
  const [health, setHealth] = useState({ ...healthRef.current });

  const updateHealth = useCallback((patch) => {
    Object.assign(healthRef.current, patch);
    setHealth({ ...healthRef.current });
  }, []);

  // Wrap setConnectionStatus to also track health metrics
  const setStatusWithHealth = useCallback((status) => {
    setConnectionStatus((prev) => {
      if (status === 'connected' && prev !== 'connected') {
        const now = Date.now();
        const wasReconnect = healthRef.current.connectedSince !== null;
        if (wasReconnect) {
          perf.markReconnectEnd();
        }
        updateHealth({
          connectedSince: now,
          disconnectedAt: null,
          reconnectCount: wasReconnect
            ? healthRef.current.reconnectCount + 1
            : healthRef.current.reconnectCount,
        });
        log.info('ws_connected', { reconnectCount: healthRef.current.reconnectCount });
      } else if (status !== 'connected' && prev === 'connected') {
        updateHealth({ disconnectedAt: Date.now() });
        perf.markReconnectStart();
        log.warn('ws_disconnected', { status });
      }
      return status;
    });
  }, [perf, updateHealth]);

  // Track inbound message throughput via the client's '*' listener
  useEffect(() => {
    const client = clientRef.current;
    const onAnyMessage = () => {
      healthRef.current.messagesReceived += 1;
      perf.recordMessage();
    };
    client.on('*', onAnyMessage);
    return () => client.off('*', onAnyMessage);
  }, [perf]);

  const ensureConnected = useCallback(async () => {
    const client = clientRef.current;
    if (client.connected) return;
    setStatusWithHealth('connecting');
    await client.connect();
    setStatusWithHealth('connected');
  }, [setStatusWithHealth]);

  // Auto-connect on mount; disconnect on unmount.
  useEffect(() => {
    const client = clientRef.current;
    setStatusWithHealth('connecting');
    client.connect()
      .then(() => setStatusWithHealth('connected'))
      .catch((err) => {
        log.warn('initial_connect_failed', { error: err?.message });
        setStatusWithHealth('error');
      });

    return () => {
      client.disconnect();
    };
  }, [setStatusWithHealth]);

  const contextValue = useMemo(() => ({
    client: clientRef.current,
    connectionStatus,
    setConnectionStatus: setStatusWithHealth,
    isConnected: connectionStatus === 'connected',
    ensureConnected,
    health,
    perf,
  }), [connectionStatus, setStatusWithHealth, ensureConnected, health, perf]);

  return (
    <ConnectionContext.Provider value={contextValue}>
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnection() {
  const ctx = useContext(ConnectionContext);
  if (!ctx) throw new Error('useConnection must be used within ConnectionProvider');
  return ctx;
}
